<template>
  <div
    v-if="isPageReady"
    class="page-content complete-registration"
  >
    <div
      v-if="isHaveActiveMembershipOrIsHadMembershipBefore"
      class="px-4 py-4 my-5 text-center"
    >
      <h1 class="page-title">
        {{ $t('registration.thanksForRegistering') }}
      </h1>
    </div>

    <div v-else>
      <div class="steps-container">
        <h3
          v-if="activeStep !== CompleteRegistrationSteps.Completed"
          class="page-title"
        >
          {{ $t('registration.finishSigningUp') }}
        </h3>

        <header-steps
          :active-step="activeStep"
          :steps="registrationSteps"
        />
      </div>

      <template v-if="activeStep === CompleteRegistrationSteps.CustomerInformation">
        <customer-info
          :customer-info="customerInfo"
          @address-changed="onCustomerInfoAddressChanged"
          @submit-clicked="onCustomerInfoSubmitted"
        />
      </template>

      <template v-if="activeStep === CompleteRegistrationSteps.FamilyMemberInformation">
        <family-member
          :family-member="familyMember"
          @submit-clicked="onFamilyMemberSubmitted"
          @skip="clickedOnFamilySkipButton"
        />
      </template>

      <template v-if="activeStep === CompleteRegistrationSteps.ChooseMembership">
        <price-planning-table
          :is-registration="false"
          :show-detailed-plan-comparison="false"
          :is-gourmet-registration="isGourmetRegistration"
          :trial-render-config="trialRenderConfig"
          @choose-membership="onMembershipSelected"
        />
      </template>

      <template v-if="activeStep === CompleteRegistrationSteps.Payment">
        <payment-method
          :customer-info="customerInfo"
          @payment-added="onPaymentAdded"
        />
      </template>

      <confirmation
        v-if="activeStep === CompleteRegistrationSteps.Completed"
        :customer-info="customerInfo"
      />
    </div>
  </div>
</template>

<script lang="ts">
// Dependencies
import { ref, onMounted, nextTick, Ref, getCurrentInstance, computed } from 'vue';
import { pageview } from 'vue-gtag';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ElLoading } from 'element-plus';
import { cloneDeep } from 'lodash';

// Utils
import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { CustomLocationType } from '@/models/address';
import { LovedOne } from '@/models/lovedOne';
import { Caller, CompleteRegistrationSteps, type CallerInput } from '@/models/caller';
import { buildUpdateCallerRequest } from '@/Utils/requestBuilders';
import { callerService } from '@/services/caller';

// Components
import HeaderSteps from './CompleteRegistration/HeaderSteps.vue';
import CustomerInfo from './CompleteRegistration/CustomerInfo.vue';
import PaymentMethod from './CompleteRegistration/PaymentMethod.vue';
import Confirmation from './CompleteRegistration/Confirmation.vue';
import FamilyMember from './CompleteRegistration/FamilyMember.vue';
import PricePlanningTable from '@/components/UIComponents/PricePlanning.vue';
import { useGtm } from '@gtm-support/vue-gtm';
import { lovedOneService } from '@/services/lovedOne';
import { useCustomer } from '@/use/customer';
import { getTrialRenderConfig } from '@/services/abTests/getTrialRenderConfig';

const getSpinner = () =>
  ElLoading.service({
    lock: true,
    text: 'Please wait...',
    spinner: 'el-icon-loading',
    background: 'rgba(0, 0, 0, 0.7)',
  });

export default {
  name: 'CompleteRegistration',

  components: {
    HeaderSteps,
    CustomerInfo,
    PricePlanningTable,
    PaymentMethod,
    Confirmation,
    FamilyMember,
  },

  beforeRouteEnter: (to: any, from: any, next: any) => {
    try {
      const encodedMetadata = atob(to.params.encodedMetadata);
      const metadata = JSON.parse(encodedMetadata);

      localStorage.setItem('access_token', metadata.token);
      localStorage.setItem('roles', JSON.stringify(metadata.roles));
      next();
    } catch (error) {
      // reject existing token
      localStorage.clear();
      console.error(error);
      next('/login');
    }
  },

  setup: () => {
    const route = useRoute();
    const instance: any = getCurrentInstance();
    const { locale, t } = useI18n();
    const { setNewCustomerInfo } = useCustomer();

    const isGourmetRegistration = computed(() => (route.meta.isGourmetRegistration as boolean) || false);
    const gtm: any = useGtm();

    gtm.trackEvent({
      event: 'page initialize',
      category: `${isGourmetRegistration.value ? 'Gourmet' : 'Gogo'}`,
      action: 'fetch',
      label: 'initialize',
    });

    const { showErrorPopupDialog } = useSwalDialog();

    const customerInfo: Ref<
      Partial<
        Caller & {
          isNeverHadMembership: boolean;
          isMembershipExpired: boolean;
        }
      >
    > = ref({});
    const familyMember: Ref<Partial<LovedOne>> = ref({
      lastName: '',
      firstName: '',
      relationship: '',
      phoneNumber: '',
      email: '',
      transactionalSms: false,
    });

    const activeStep: Ref<CompleteRegistrationSteps> = ref(CompleteRegistrationSteps.CustomerInformation);
    const isCustomerLoaded = ref(false);
    const isHadMembershipBeforeRef = ref(false);
    const isHaveActiveMembershipRef = ref(false);
    const registrationSteps: Ref<
      {
        title: string;
        description: string;
      }[]
    > = ref([]);
    const trialRenderConfig: Ref<ReturnType<typeof getTrialRenderConfig>> = ref(null);

    const initGooglePlaces = async () => instance.ctx.$gmapApiPromiseLazy();

    const loadCustomer = async () => {
      const response = await callerService.fetchCustomerInfo({});
      customerInfo.value = cloneDeep({
        ...response,
        isNeverHadMembership: response.isNeverHadMembership ?? undefined,
        isMembershipExpired: response.isMembershipExpired ?? undefined,
      });
      isCustomerLoaded.value = true;
      gtm.trackEvent({
        event: 'ready',
        category: `${isGourmetRegistration.value ? 'Gourmet' : 'Gogo'}`,
        action: 'view',
        label: 'page ready',
      });
      isHadMembershipBeforeRef.value = !customerInfo.value.isNeverHadMembership;
      isHaveActiveMembershipRef.value = !customerInfo.value.isMembershipExpired;
      gtm.trackEvent({
        event: 'fetch',
        category: `${isGourmetRegistration.value ? 'Gourmet' : 'Gogo'}`,
        action: 'fetch',
        label: 'customer data has been fetched',
      });

      // Fetch trial render config
      if (customerInfo.value.trialInfo) {
        trialRenderConfig.value = getTrialRenderConfig(customerInfo.value.trialInfo);
      }

      await loadLovedOne();
    };

    /**
     * This method should be called when the loadCustomer is completed
     */
    const loadLovedOne = async () => {
      // since this is a registration page,
      // a user might created a loved one but didn't complete
      // so we load the loved one if it exists
      if (!customerInfo.value.id) {
        return;
      }

      const { rows } = await lovedOneService.fetchLovedOnes(customerInfo.value.id);
      if (rows !== null && rows.length > 0) {
        familyMember.value = cloneDeep(rows[0]);
      }
      gtm.trackEvent({
        event: 'data fetch',
        category: `${isGourmetRegistration.value ? 'Gourmet' : 'Gogo'}`,
        action: 'fetch',
        label: 'Loved one loaded',
      });
    };

    // properties
    const isPageReady = computed(() => isCustomerLoaded.value);
    const isHaveActiveMembershipOrIsHadMembershipBefore = computed(() => isHadMembershipBeforeRef.value || isHaveActiveMembershipRef.value);

    // event handlers
    const onCustomerInfoSubmitted = async () => {
      const isSuccess = await save();
      if (isSuccess) {
        gtm.trackEvent({
          event: 'submit',
          category: `${isGourmetRegistration.value ? 'Gourmet' : 'Gogo'}`,
          action: 'click',
          label: 'customer info submitted',
        });
        activeStep.value = CompleteRegistrationSteps.FamilyMemberInformation;
      }
    };

    const onFamilyMemberSubmitted = async () => {
      const isSuccess = await saveFamilyMember();
      if (isSuccess) {
        gtm.trackEvent({
          event: 'submit',
          category: `${isGourmetRegistration.value ? 'Gourmet' : 'Gogo'}`,
          action: 'click',
          label: 'family member submitted',
        });
        activeStep.value = CompleteRegistrationSteps.ChooseMembership;
      }
    };

    const onCustomerInfoAddressChanged = (address: CustomLocationType | null) => {
      customerInfo.value = {
        ...customerInfo.value,
        streetAddress: address?.streetAddress || '',
        streetAddress2: '',
        city: address?.city || '',
        state: address?.state || '',
        zip: address?.zip || '',
        country: address?.country || '',
        latitude: address?.latitude ? Number(address.latitude) : null,
        longitude: address?.longitude ? Number(address.longitude) : null,
      };
    };

    const onMembershipSelected = async (plan: string) => {
      customerInfo.value.registerPlan = plan;

      const isSuccess = await save();
      if (isSuccess) {
        gtm.trackEvent({
          event: 'submit',
          category: `${isGourmetRegistration.value ? 'Gourmet' : 'Gogo'}`,
          action: 'click',
          label: 'membership selected',
        });
        activeStep.value = CompleteRegistrationSteps.Payment;
      }
    };

    const onPaymentAdded = async () => {
      const isSuccess = await save();
      if (isSuccess) {
        gtm.trackEvent({
          event: 'submit',
          category: `${isGourmetRegistration.value ? 'Gourmet' : 'Gogo'}`,
          action: 'click',
          label: 'payment added',
        });
        activeStep.value = CompleteRegistrationSteps.Completed;
        gtm.trackEvent({
          event: 'page view',
          category: `${isGourmetRegistration.value ? 'Gourmet' : 'Gogo'}`,
          action: 'view',
          label: 'Complete registration screen',
        });
      }
    };

    // helpers
    const save = async () => {
      const spinner = getSpinner();
      let isSuccess: boolean;

      try {
        const data = buildUpdateCallerRequest(customerInfo.value) as CallerInput;

        const response = await callerService.updateCaller({
          callerId: customerInfo.value.id!,
          data,
        });
        setNewCustomerInfo(response);
        isSuccess = true;
      } catch (error) {
        isSuccess = false;
        const errorText = util.getErrorResponse(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        spinner.close();
      }

      return isSuccess;
    };

    const saveFamilyMember = async () => {
      const spinner = getSpinner();
      let isSuccess: boolean;

      try {
        if (familyMember.value.id) {
          await lovedOneService.updateLovedOne({
            id: familyMember.value.id,
            data: familyMember.value,
          });
        } else {
          await lovedOneService.addLovedOne({
            callerId: customerInfo.value.id,
            data: familyMember.value,
          });
        }

        isSuccess = true;
      } catch (error) {
        isSuccess = false;
        const errorText = util.getErrorResponse(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        spinner.close();
      }

      return isSuccess;
    };

    onMounted(async () => {
      locale.value = (route.query?.language as string) || 'en';
      registrationSteps.value = [
        {
          title: t('registration.step', { step: 1 }),
          description: t('registration.customerInformation'),
        },
        {
          title: t('registration.step', { step: 2 }),
          description: t('registration.familyMember'),
        },
        {
          title: t('registration.step', { step: 3 }),
          description: t('registration.chooseMembership'),
        },
        {
          title: t('registration.step', { step: 4 }),
          description: t('registration.billingInformation'),
        },
        {
          title: t('registration.step', { step: 5 }),
          description: t('registration.done'),
        },
      ];

      pageview({
        page_title: 'Complete Registration',
        page_path: '/complete-registration',
      });
      await nextTick();
      util.moveToTop();

      const spinner = getSpinner();

      try {
        await initGooglePlaces();
        await loadCustomer();
      } catch (error: any) {
        showErrorPopupDialog({
          text: 'An error had occured. Please try again later.',
        });
      } finally {
        spinner.close();
      }
    });

    const clickedOnFamilySkipButton = () => {
      gtm.trackEvent({
        event: 'skip',
        category: `${isGourmetRegistration.value ? 'Gourmet' : 'Gogo'}`,
        action: 'click',
        label: 'Family member skipped',
      });
      activeStep.value = CompleteRegistrationSteps.ChooseMembership;
    };

    return {
      CompleteRegistrationSteps,

      activeStep,

      customerInfo,
      familyMember,
      trialRenderConfig,
      isPageReady,
      isHaveActiveMembershipOrIsHadMembershipBefore,
      registrationSteps,
      isGourmetRegistration,

      onCustomerInfoSubmitted,
      onFamilyMemberSubmitted,
      onMembershipSelected,
      onCustomerInfoAddressChanged,
      onPaymentAdded,
      clickedOnFamilySkipButton,
    };
  },
};
</script>

<style lang="scss">
.page-content.complete-registration {
  max-width: 1000px;
  margin: 0 auto;
  padding-bottom: 20px;

  form {
    padding: 20px 20px 10px;
    box-shadow:
      rgb(0 0 0 / 12%) 0px 1px 3px 0px,
      rgb(0 0 0 / 20%) 0px 2px 1px -1px,
      rgb(0 0 0 / 14%) 0px 1px 1px 0px;
  }

  .page-title {
    font-size: 32px;
    line-height: 32px;
    margin-top: 40px;
    margin-bottom: 20px;
    font-weight: bold;
    text-align: center;

    @media (max-width: 576px) {
      font-size: 24px;
      line-height: 28px;
      margin-top: 20px;
      margin-bottom: 10px;
    }
  }

  @media (max-width: 576px) {
    .m-mt-10 {
      margin-top: 10px !important;
    }

    .m-mt-30 {
      margin-top: 30px;
    }

    .btn.btn-purple {
      width: 100%;
    }

    .btn.btn-yellow {
      width: 100%;
    }

    .steps-container {
      margin-bottom: 0;
    }

    form {
      box-shadow: none;
    }
  }
}
</style>
